import axios from "axios";

const API = `${window.location.origin}/api`;

// Configure axios to always send cookies (critical for cross-origin httpOnly cookies)
axios.defaults.withCredentials = true;
axios.defaults.timeout = 15000; // 15s timeout

// Auto-refresh on 401
let isRefreshing = false;
let refreshQueue = [];

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;
    // If 401 and not already retrying and not a refresh/login/register request
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/login") &&
      !originalRequest.url?.includes("/auth/register") &&
      !originalRequest.url?.includes("/auth/google/session") &&
      !originalRequest.url?.includes("/auth/refresh")
    ) {
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(`${API}/auth/refresh`);
        // Retry all queued requests
        refreshQueue.forEach(({ resolve }) => resolve());
        refreshQueue = [];
        return axios(originalRequest);
      } catch (refreshError) {
        refreshQueue.forEach(({ reject }) => reject(refreshError));
        refreshQueue = [];
        // Refresh failed — force re-login
        window.dispatchEvent(new Event("auth-expired"));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Format FastAPI error detail for display.
 * FastAPI 422 returns array of {msg,...} objects, not a string.
 */
export function getErrorMessage(err) {
  if (!err?.response) return "Cannot reach the server. Please check your internet connection and try again.";
  return formatApiError(err.response.data?.detail);
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(". ") || "Invalid request format";
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function loginUser(email, password) {
  const res = await axios.post(`${API}/auth/login`, { email, password });
  return res.data;
}

export async function registerUser(email, password, name) {
  const res = await axios.post(`${API}/auth/register`, { email, password, name });
  return res.data;
}

export async function logoutUser() {
  await axios.post(`${API}/auth/logout`);
}

export async function getMe() {
  const res = await axios.get(`${API}/auth/me`);
  return res.data;
}

export async function forgotPassword(email) {
  const res = await axios.post(`${API}/auth/forgot-password`, { email });
  return res.data;
}

export async function resetPassword(email, otp, newPassword) {
  const res = await axios.post(`${API}/auth/reset-password`, { email, otp, new_password: newPassword });
  return res.data;
}

export async function findEmail(name) {
  const res = await axios.post(`${API}/auth/find-email`, { name });
  return res.data;
}

export async function changePassword(currentPassword, newPassword) {
  const res = await axios.post(`${API}/auth/change-password`, {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return res.data;
}
